
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box, Grid, Card, CardContent, Typography, CircularProgress,
  Chip, FormControl, InputLabel, Select, MenuItem, Table,
  TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import {
  PeopleAlt, LocalShipping, SwapVert, Assignment,
  Inventory2, Refresh, SupportAgent, Store, Factory,
} from "@mui/icons-material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, Title, Tooltip, Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import axiosClient from "../../configs/axios";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// ─── Design tokens (same as PlantDashboard) ───────────────────────────────────
const C = {
  navy:      "#1B3A6B",
  accent:    "#2563EB",
  bg:        "#F0F4F8",
  card:      "#FFFFFF",
  border:    "#E2E8F0",
  textMuted: "#64748B",
  success:   "#16A34A",
  warn:      "#D97706",
  err:       "#DC2626",
  infoBg:    "#EFF6FF",
};

const CHART_COLORS = { delivery: "#2563EB", collection: "#16A34A" };
const REFRESH_INTERVAL_MS = 60_000;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getLast7Days() {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d.toISOString().split("T")[0];
  });
}
function formatDateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", {
    month: "short", day: "numeric", timeZone: "UTC",
  });
}

// ─── KPI Card (same as PlantDashboard) ────────────────────────────────────────
const KpiCard = ({ label, value, unit, icon: Icon, accentColor = C.accent, loading }) => (
  <Card elevation={0} sx={{
    border: `1px solid ${C.border}`, borderRadius: "10px",
    borderLeft: `3px solid ${accentColor}`, background: C.card, height: "100%",
  }}>
    <CardContent sx={{ p: "12px 14px !important" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: 10, color: C.textMuted, fontWeight: 500, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.5 }}>
            {label}
          </Typography>
          {loading ? (
            <CircularProgress size={18} sx={{ color: accentColor, mt: 0.5 }} />
          ) : (
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: C.navy, lineHeight: 1 }}>
              {value ?? "—"}
            </Typography>
          )}
          {unit && !loading && (
            <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.3 }}>{unit}</Typography>
          )}
        </Box>
        <Icon sx={{ fontSize: 26, color: accentColor, opacity: 0.15, mt: 0.3 }} />
      </Box>
    </CardContent>
  </Card>
);

// ─── Section Card wrapper ─────────────────────────────────────────────────────
const SectionCard = ({ icon: Icon, title, children, loading }) => (
  <Card elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", background: C.card }}>
    <CardContent sx={{ p: "14px 16px !important" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
        <Icon sx={{ fontSize: 16, color: C.accent }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{title}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ color: C.accent }} />
        </Box>
      ) : children}
    </CardContent>
  </Card>
);

// ─── Table styles ─────────────────────────────────────────────────────────────
const thSx = { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", py: 0.75, px: 1.5, borderBottom: `1px solid ${C.border}`, background: "#F8FAFC" };
const tdSx = { fontSize: 12, color: C.navy, py: 0.75, px: 1.5, borderBottom: `1px solid ${C.border}` };

const Badge = ({ value, color }) => (
  <Box component="span" sx={{ display: "inline-block", px: 1, py: 0.2, borderRadius: "4px", fontSize: 11, fontWeight: 600, background: `${color}18`, color }}>
    {value}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SalesOfficerDashboard = () => {

  const userInfo = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("loginUser") || "{}")?.result?.userInfo || {}; }
    catch { return {}; }
  }, []);
const assignedOfficerId = userInfo?.sales_officer_id ?? "";
const isSalesRole       = userInfo?.user_role === "sales";



  const [officers, setOfficers]                   = useState([]);
  const [selectedOfficerId, setSelectedOfficerId] = useState(assignedOfficerId ? String(assignedOfficerId) : "");
  const [dashData, setDashData]                   = useState(null);
  const [detailData, setDetailData]               = useState(null);
  const [loading, setLoading]                     = useState(true);
  const [chartLoading, setChartLoading]           = useState(false);
  const [lastRefreshed, setLastRefreshed]         = useState(null);
  const timerRef = useRef(null);

  // Officers list — GET /api/account-manager/list
  useEffect(() => {
    axiosClient.get("/account-manager/list")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    setOfficers(list);
if (!selectedOfficerId && list.length > 0) {
  if (isSalesRole && assignedOfficerId) {
    setSelectedOfficerId(String(assignedOfficerId));
  } else {
    setSelectedOfficerId(String(list[0].sales_officer_id));
  }
}
      })
      .catch(console.error);
  }, []); // eslint-disable-line

  // Dashboard data — GET /api/dashboard/sales-officer + /api/dashboard/sales-officer-depot-plant
  const fetchData = useCallback(async () => {
    if (!selectedOfficerId) return;
    setChartLoading(true);
    try {
      const params = { sales_officer_id: selectedOfficerId };
      const [summaryRes, detailRes] = await Promise.allSettled([
        axiosClient.get("/dashboard/sales-officer",             { params }),
        axiosClient.get("/dashboard/sales-officer-depot-plant", { params }),
      ]);
      if (summaryRes.status === "fulfilled") setDashData(summaryRes.value.data);
      if (detailRes.status  === "fulfilled") setDetailData(detailRes.value.data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("SalesOfficerDashboard fetchData error:", err);
    } finally {
      setChartLoading(false);
      setLoading(false);
    }
  }, [selectedOfficerId]);

  useEffect(() => {
    fetchData();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  // Chart
  const chartData = useMemo(() => {
    const last7 = getLast7Days();
    return {
      labels: last7.map(formatDateLabel),
      datasets: [
        { label: "Cylinders Delivered", data: dashData?.trend?.deliveries  ?? Array(7).fill(0), backgroundColor: CHART_COLORS.delivery,   borderWidth: 0, borderRadius: 3 },
        { label: "Cylinders Collected", data: dashData?.trend?.collections ?? Array(7).fill(0), backgroundColor: CHART_COLORS.collection, borderWidth: 0, borderRadius: 3 },
      ],
    };
  }, [dashData]);

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    layout: { padding: { top: 24 } },
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
      datalabels: { anchor: "end", align: "end", offset: 4, color: C.navy, font: { size: 10, weight: "600" }, formatter: (v) => v === 0 ? "" : v },
    },
    scales: {
      x: { ticks: { autoSkip: false, maxRotation: 0, font: { size: 11 } }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "rgba(0,0,0,0.05)" } },
    },
  };

  const selectedOfficerName = useMemo(() => {
    const o = officers.find((o) => String(o.sales_officer_id) === selectedOfficerId);
    return o?.sales_officer_name ?? "Sales Officer";
  }, [officers, selectedOfficerId]);

  const s = dashData?.summary;
  const kpis = [
    { label: "Total Customers",     value: loading ? null : s?.total_customers,   unit: "assigned",     icon: PeopleAlt,     color: C.accent  },
    { label: "Today's Deliveries",  value: loading ? null : s?.today_deliveries,  unit: "cylinders",    icon: LocalShipping, color: C.navy    },
    { label: "Today's Collections", value: loading ? null : s?.today_collections, unit: "cylinders",    icon: SwapVert,      color: C.success },
    { label: "Pending Orders",      value: loading ? null : s?.pending_orders,    unit: "sales orders", icon: Assignment,    color: C.warn    },
    { label: "Customer Holding",    value: loading ? null : s?.customer_holding,  unit: "cylinders",    icon: Inventory2,    color: C.err     },
  ];

  return (
    <Box sx={{ p: { xs: "12px", sm: "16px", md: "20px" }, background: C.bg, minHeight: "100vh" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: C.navy }}>Sales Officer Dashboard</Typography>
          <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.3 }}>CYL Tracking System — {selectedOfficerName}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {!isSalesRole && (officers.length > 1 || !assignedOfficerId) && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: 12 }}>Sales Officer</InputLabel>
              <Select value={selectedOfficerId} label="Sales Officer"
                onChange={(e) => setSelectedOfficerId(e.target.value)}
                sx={{ fontSize: 12, background: C.card }}>
                {officers.map((o) => (
                  <MenuItem key={o.sales_officer_id} value={String(o.sales_officer_id)} sx={{ fontSize: 12 }}>
                    {o.sales_officer_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {lastRefreshed && (
            <Typography sx={{ fontSize: 11, color: C.textMuted }}>
              Updated {lastRefreshed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          )}
          <Chip label="Sales" size="small"
            icon={<SupportAgent sx={{ fontSize: "13px !important", color: `${C.accent} !important` }} />}
            sx={{ background: C.infoBg, color: C.accent, border: `0.5px solid #BFDBFE`, fontSize: 11, fontWeight: 500 }}
          />
          <Box component="span" onClick={fetchData} title="Refresh now"
            sx={{ cursor: "pointer", color: C.textMuted, display: "flex", alignItems: "center", "&:hover": { color: C.accent } }}>
            <Refresh sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>

      {/* ── KPI Row ── */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={2.4} key={k.label}>
            <KpiCard label={k.label} value={k.value} unit={k.unit} icon={k.icon} accentColor={k.color} loading={loading && k.value === null} />
          </Grid>
        ))}
      </Grid>

      {/* ── Depot Table + Plant Table ── */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>

        {/* Depot Summary */}
        <Grid item xs={12} md={7}>
          <SectionCard icon={Store} title="Depot-wise Cylinder Status (Today)" loading={loading}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Depot</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Full</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Empty</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>In-Transit</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Total</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Today DC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailData?.depots ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...tdSx, textAlign: "center", color: C.textMuted }}>No depots found</TableCell>
                    </TableRow>
                  ) : (detailData?.depots ?? []).map((d) => (
                    <TableRow key={d.depot_id} sx={{ "&:hover": { background: "#F8FAFC" } }}>
                      <TableCell sx={{ ...tdSx, fontWeight: 600 }}>{d.depot_name}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={d.full}       color={C.success} /></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={d.empty}      color={C.textMuted} /></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={d.in_transit} color={C.warn} /></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={d.total}      color={C.navy} /></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={d.today_dc}   color={C.accent} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>

        {/* Plant Summary */}
        <Grid item xs={12} md={5}>
          <SectionCard icon={Factory} title="Plant Summary (Today)" loading={loading}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Plant</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Production</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Full</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "center" }}>Empty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailData?.plants ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ ...tdSx, textAlign: "center", color: C.textMuted }}>No plants found</TableCell>
                    </TableRow>
                  ) : (detailData?.plants ?? []).map((p) => (
                    <TableRow key={p.plant_id} sx={{ "&:hover": { background: "#F8FAFC" } }}>
                      <TableCell sx={{ ...tdSx, fontWeight: 600 }}>{p.plant_name}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                        <Box>
                          <Badge value={`${p.today_production} cyls`} color={C.accent} />
                          <Typography sx={{ fontSize: 10, color: C.textMuted, mt: 0.3 }}>{p.today_qty} kg</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={p.full}  color={C.success} /></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}><Badge value={p.empty} color={C.textMuted} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── 7-Day Chart ── */}
      {/* <Card elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", background: C.card }}>
        <CardContent sx={{ p: "14px 16px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <LocalShipping sx={{ fontSize: 16, color: C.accent }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>
                7-day delivery vs collection — {selectedOfficerName}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {[{ label: "Cylinders Delivered", color: CHART_COLORS.delivery }, { label: "Cylinders Collected", color: CHART_COLORS.collection }].map((l) => (
                <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: l.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 11, color: C.textMuted }}>{l.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {chartLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
              <CircularProgress size={28} sx={{ color: C.accent }} />
            </Box>
          ) : (
            <Box sx={{ position: "relative", width: "100%", height: { xs: 220, sm: 260 } }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
          )}
        </CardContent>
      </Card> */}
    </Box>
  );
};

export default SalesOfficerDashboard;